import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCrmOrgStore } from '../../../store/crmOrg.store';
import contactsService from '../../../services/crm/contactsService';
import companiesService from '../../../services/crm/companiesService';
import dealsService from '../../../services/crm/dealsService';

interface DashboardMetrics {
  totalContacts: number;
  activeCompanies: number;
  openDealsValue: number;
  thisMonthDeals: number;
  conversionRate: number | null;
}

const CustomerCrmHubHeader: React.FC = () => {
  const { activeOrgId } = useCrmOrgStore();
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalContacts: 0,
    activeCompanies: 0,
    openDealsValue: 0,
    thisMonthDeals: 0,
    conversionRate: null
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeOrgId) return;

    const fetchMetrics = async () => {
      setLoading(true);
      try {
        const [contactsRes, companiesRes, dealsRes] = await Promise.all([
          contactsService.list({ organization_id: activeOrgId, limit: 1000, offset: 0 }),
          companiesService.list({ organization_id: activeOrgId, limit: 1000, offset: 0 }),
          dealsService.list({ organization_id: activeOrgId, limit: 1000, offset: 0 })
        ]);

        const contacts = contactsRes.data || [];
        const companies = companiesRes.data || [];
        const deals = dealsRes.data || [];
        
        // Calculate metrics
        const activeCompaniesCount = companies.filter((c: any) => 
          c.status === 'active' || !c.status
        ).length;

        const openDeals = deals.filter((d: any) => 
          d.stage !== 'closed_won' && d.stage !== 'closed_lost'
        );
        const openDealsTotal = openDeals.reduce((sum: number, d: any) => 
          sum + (d.amount || 0), 0
        );

        // This month deals
        const thisMonth = new Date();
        thisMonth.setDate(1);
        thisMonth.setHours(0, 0, 0, 0);
        const thisMonthCount = deals.filter((d: any) => {
          const createdAt = new Date(d.created_at);
          return createdAt >= thisMonth;
        }).length;

        // Conversion rate
        const closedWon = deals.filter((d: any) => d.stage === 'closed_won').length;
        const closedLost = deals.filter((d: any) => d.stage === 'closed_lost').length;
        const totalClosed = closedWon + closedLost;
        const conversionRate = totalClosed > 0 ? Math.round((closedWon / totalClosed) * 100) : null;

        setMetrics({
          totalContacts: contacts.length,
          activeCompanies: activeCompaniesCount,
          openDealsValue: openDealsTotal,
          thisMonthDeals: thisMonthCount,
          conversionRate
        });
      } catch (error) {
        console.error('Failed to fetch CRM metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    void fetchMetrics();
  }, [activeOrgId]);

  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
      {/* Title */}
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer CRM</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage contacts, companies, and deals in one unified workspace
          </p>
        </div>
        <Link
          to="/help"
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
        >
          ❓ Help
        </Link>
      </div>

      {/* Metrics Dashboard */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <MetricCard
          label="Total Contacts"
          value={metrics.totalContacts}
          icon="📧"
          loading={loading}
        />
        <MetricCard
          label="Active Companies"
          value={metrics.activeCompanies}
          icon="🏢"
          loading={loading}
        />
        <MetricCard
          label="Open Deals"
          value={`$${(metrics.openDealsValue / 1000).toFixed(0)}K`}
          icon="💰"
          loading={loading}
          isValue
        />
        <MetricCard
          label="This Month"
          value={metrics.thisMonthDeals}
          icon="📈"
          loading={loading}
        />
        <MetricCard
          label="Win Rate"
          value={metrics.conversionRate !== null ? `${metrics.conversionRate}%` : '—'}
          icon="✅"
          loading={loading}
          isValue
        />
      </div>
    </div>
  );
};

interface MetricCardProps {
  label: string;
  value: number | string;
  icon: string;
  loading: boolean;
  isValue?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, icon, loading, isValue = false }) => {
  return (
    <div className="rounded-xl bg-gray-50 p-4">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">
            {loading ? (
              <span className="inline-block h-8 w-16 animate-pulse rounded bg-gray-200"></span>
            ) : isValue ? (
              value
            ) : (
              new Intl.NumberFormat().format(value as number)
            )}
          </p>
        </div>
        <div className="text-2xl">{icon}</div>
      </div>
    </div>
  );
};

export default CustomerCrmHubHeader;
